/* eslint-disable max-lines */
import React, { useEffect, useMemo, useState } from 'react'
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft'
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight'
import FirstPageIcon from '@mui/icons-material/FirstPage'
import LastPageIcon from '@mui/icons-material/LastPage'
import IosShareIcon from '@mui/icons-material/IosShare'
import AutorenewIcon from '@mui/icons-material/Autorenew'
import ZoomInIcon from '@mui/icons-material/ZoomIn'
import { IconButton } from '@mui/material'
import OpenInFullIcon from '@mui/icons-material/OpenInFull'
import moment from 'moment'
import styled, { css } from 'styled-components'
import Calendar from 'components/Calendar'
import HomeownerDayNotification from 'pages/shared/HomeownerAlert/HomeownerDayNotification'
import KeyboardAccessible from 'components/KeyboardAccessible'
import Tooltip from 'components/Tooltip'
import { Button } from '@nexiahome/ui-common/components/primitives/buttons'
import useBrowserCompatibility from 'hooks/useBrowserCompatibility'
import { longDate } from 'helpers/formatters'
import GA4 from 'helpers/ga4'
import { screenSize } from 'styles/shared'
import { color } from 'styles/colors'
import { usePremiumFeaturesContext } from 'hooks/PremiumFeaturesContext'

const DATE_FORMAT = 'YYYY-MM-DD'
const DEFAULT_MIN_DATE = moment().subtract(30, 'days').format(DATE_FORMAT)

type Props = {
  date: string
  displayResetZoom: boolean
  zoomResetCallback: () => void
  refreshCallback: () => void
  dateChangedCallback: (date: string) => void
  minDate?: string
  rthVariant: 'Awair' | 'RTH'
  showDoubleClickInfo?: boolean
  freezeMouseHover?: boolean
  isNoData?: boolean
  isRTHDataLoaded?: boolean
  isFullScreen?: boolean
  onExportCallback: (selectedDate) => void
  fullScreenIconClickedCallback: () => void
  displayHomeownerNotification?: boolean
}

const RthHeaderControl = ({
  date, displayResetZoom, zoomResetCallback, refreshCallback, dateChangedCallback, rthVariant, freezeMouseHover, minDate = DEFAULT_MIN_DATE,
  isNoData, isRTHDataLoaded, isFullScreen, onExportCallback, fullScreenIconClickedCallback, displayHomeownerNotification
}: Props) => {
  const displayRefresh = useMemo(() => moment(date, DATE_FORMAT).isSame(moment(), 'day'), [date])
  const oneDayBeforeTheDate = moment(date, DATE_FORMAT).subtract(1, 'days').format(DATE_FORMAT)
  const oneDayAfterTheDate = moment(date, DATE_FORMAT).add(1, 'days').format(DATE_FORMAT)
  const today = moment().format(DATE_FORMAT)
  const [selectedDate, setSelectedDate] = useState(date)
  const { isTouchEnabled } = useBrowserCompatibility()
  const { hasAccess, showUpgrade } = usePremiumFeaturesContext()

  useEffect(() => {
    setSelectedDate(date)
  }, [date])

  const sendGoogleAnalyticsChangeDate = (label) => {
    const action = rthVariant === 'Awair' ? 'Awair Chart' : 'RTH'
    const eventData = { category: 'Customer Detail Page', action: `Change ${action} Date`, label }
    GA4.sendEvent(eventData)
  }

  const onDateChange = (date, gaLabel) => {
    sendGoogleAnalyticsChangeDate(gaLabel)
    dateChangedCallback(date)
    setSelectedDate(date)
  }

  const extendedRthgEnabled = hasAccess('extended_rthg')

  return (
    <S.RthHeaderControl>
      <S.TopRow>
        {displayHomeownerNotification
          ? <S.HomeownerLastNotified date={new Date(date)} />
          : null
        }
        {displayResetZoom
          ? <Button decoration="tertiary" styles={{ button: { marginLeft: 5 } }} onPress={zoomResetCallback}><S.ButtonText><S.ZoomInIcon /><span>reset zoom</span></S.ButtonText></Button>
          : null
        }
        {displayRefresh
          ? <Button decoration="tertiary" styles={{ button: { marginLeft: 5 } }} onPress={refreshCallback}><S.ButtonText><S.AutorenewIcon /><span>refresh</span></S.ButtonText></Button>
          : null
        }
        {!isNoData && isRTHDataLoaded && (
          <IconButton onClick={() => onExportCallback(selectedDate)} color="inherit"><S.IosShareIcon /></IconButton>
        )}
        {!isFullScreen && rthVariant === 'RTH' &&
          <Tooltip title={'Expand graph to full screen.'}>
            <IconButton onClick={fullScreenIconClickedCallback} color="inherit"><S.OpenInFullIcon /></IconButton>
          </Tooltip>
        }
      </S.TopRow>
      <S.Info>
        <S.Instructions>Click and drag to zoom.</S.Instructions>
        {!isTouchEnabled && !freezeMouseHover && <S.Instructions>Double-click to freeze the data in the graph.</S.Instructions>}
        {!isTouchEnabled && freezeMouseHover && <S.Instructions>Click anywhere in the graph to unfreeze the data.</S.Instructions>}
      </S.Info>
      <S.BottomRow>
        {(rthVariant === 'Awair' || !extendedRthgEnabled) &&
          <S.DayNavigationControl
            onClick={date === minDate ? () => { } : () => { onDateChange(minDate, 'First') }}
            enabled={date !== minDate}
          >
            <Tooltip title={showUpgrade('extended_rthg') && date === minDate && rthVariant === 'RTH' ? 'Upgrade to Diagnostics Premium for extended Runtime Data' : ''}>
              <S.FastBackwardIcon aria-label="go to first day" disabled={date === minDate} />
            </Tooltip>
          </S.DayNavigationControl>
        }
        <S.DayNavigationControl
          onClick={(date === minDate && rthVariant === 'RTH' && !extendedRthgEnabled) ? () => { } : () => { onDateChange(oneDayBeforeTheDate, 'Previous') }}
          enabled={date !== minDate || rthVariant === 'Awair' || extendedRthgEnabled}
        >
          <Tooltip title={showUpgrade('extended_rthg') && date === minDate && !extendedRthgEnabled && rthVariant === 'RTH' ? 'Upgrade to Diagnostics Premium for extended Runtime Data' : ''}>
            <S.BackwardIcon aria-label="go to previous day" disabled={(date === minDate && rthVariant === 'RTH' && !extendedRthgEnabled)} />
          </Tooltip>
        </S.DayNavigationControl>
        <S.Container>
          <S.Date>{longDate(date)}</S.Date>
          <Calendar
            date={date}
            minDate={!extendedRthgEnabled ? moment(minDate, 'YYYY-MM-DD') : null}
            onDateChangeCallback={(date) => onDateChange(date, 'Specific')}
            showCalendarIcon
            rightAlign
            variant="textOnly"
          />
        </S.Container>
        <S.DayNavigationControl
          onClick={date === today ? () => { } : () => { onDateChange(oneDayAfterTheDate, 'Next') }}
          enabled={date !== today}
        >
          <S.ForwardIcon aria-label="go to next day" disabled={date === today} />
        </S.DayNavigationControl>
        <S.DayNavigationControl
          onClick={date === today ? () => { } : () => { onDateChange(today, 'Last') }}
          enabled={date !== today}
        >
          <S.FastForwardIcon aria-label="go to today" disabled={date === today} />
        </S.DayNavigationControl>
      </S.BottomRow>
    </S.RthHeaderControl>
  )
}

export default RthHeaderControl

const iconStyle = css<{ disabled: boolean }>`
  font-size: 1.9em;
  color: ${({ disabled }) => disabled ? color('actionableIconDisabled') : color('actionableIcon')};

  &:hover {
    cursor: ${({ disabled }) => disabled ? 'initial' : 'pointer'};
    background-color: ${({ disabled }) => disabled ? color('paneBackground') : color('rthChartIconHoverBackground')};
    border-radius: 3px;
  }
`
const iconButtonStyle = css`
  color: ${color('primaryButton')};
  font-size: 1.3em;

  &:hover {
    color: ${color('primaryButtonHover')};
  }
`
const S = {
  RthHeaderControl: styled.div`
    margin: 10px 10px;
  `,
  TopRow: styled.div`
    display: flex;
    justify-content: end;
    gap: 10px;
  `,
  BottomRow: styled.div`
    width: fit-content;
    margin: auto auto;
    display: flex;
    align-items: center;
    gap: 5px;
  `,
  DayNavigationControl: styled(KeyboardAccessible)`
    display: inline-block;
    margin: auto 5px;
  `,
  FastBackwardIcon: styled(FirstPageIcon) <{ disabled: boolean }>`
    margin-right: 10px;
    ${iconStyle}
  `,
  BackwardIcon: styled(KeyboardArrowLeftIcon) <{ disabled: boolean }>`
    ${iconStyle}
  `,
  ForwardIcon: styled(KeyboardArrowRightIcon) <{ disabled: boolean }>`
    ${iconStyle}
  `,
  FastForwardIcon: styled(LastPageIcon) <{ disabled: boolean }>`
    margin-left: 10px;
    ${iconStyle}
  `,
  Info: styled.div`
    min-width: 270px;
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-bottom: 8px;

    @media ${screenSize.small} {
      width: initial;
    }
  `,
  Date: styled.div`
    font-size: 20px;
    text-transform: uppercase;
    color: ${color('rthChartDate')};
    font-weight: bold;
  `,
  Instructions: styled.div`
    color: ${color('rthHelpText')};
    font-weight: 500;
  `,
  Container: styled.div`
    display: flex;
    margin: 0px 35px;
  `,
  IosShareIcon: styled(IosShareIcon)`
    ${iconButtonStyle}
  `,
  AutorenewIcon: styled(AutorenewIcon)`
    font-size: 1.8em;
  `,
  ZoomInIcon: styled(ZoomInIcon)`
    font-size: 1.9em;
  `,
  OpenInFullIcon: styled(OpenInFullIcon)`
    ${iconButtonStyle}
  `,
  ButtonText: styled.div`
    color: ${color('primaryButton')};
    margin-bottom: -8px;
    text-transform: uppercase;
    font-weight: 700;
    font-size: 15px;
    display: flex;
    align-items: center;
    gap: 5px;

    &:hover {
      color: ${color('primaryButtonHover')};
    }
  `,
  HomeownerLastNotified: styled(HomeownerDayNotification)`
    margin-right: auto;
  `
}
